import apiURL from "libs/apiUrl";
import type { AgeCategoryEnum, CompetitionTier, CompetitionType, GenderEnum } from "libs/apiClient";

export type SelectOption<T extends string = string> = {
	value: T;
	label: string;
};

export const COMPETITION_TYPE_OPTIONS: SelectOption<CompetitionType>[] = [
	{ value: "domestic_league", label: "Domestic league" },
	{ value: "domestic_cup", label: "Domestic cup" },
	{ value: "club_tournament", label: "Club tournament" },
	{ value: "national_team_tournament", label: "National team tournament" },
];

export const COMPETITION_TIER_OPTIONS: SelectOption<CompetitionTier>[] = [
	{ value: "national", label: "National" },
	{ value: "regional", label: "Regional" },
	{ value: "district", label: "District" },
];

export const COMPETITION_GENDER_OPTIONS: SelectOption<GenderEnum>[] = [
	{ value: "Male", label: "Male" },
	{ value: "Female", label: "Female" },
];

export const COMPETITION_AGE_GROUP_OPTIONS: SelectOption<AgeCategoryEnum>[] = [
	"U6",
	"U7",
	"U8",
	"U9",
	"U10",
	"U11",
	"U12",
	"U13",
	"U14",
	"U15",
	"U16",
	"U17",
	"U18",
	"U19",
	"U20",
	"U21",
	"U23",
	"SENIOR",
	"N2",
	"N3",
].map((value) => ({ value: value as AgeCategoryEnum, label: value }));

export const COMPETITION_FORMAT_OPTIONS: SelectOption[] = [
	{ value: "11x11", label: "11x11" },
	{ value: "9x9", label: "9x9" },
	{ value: "7x7", label: "7x7" },
	{ value: "5x5", label: "5x5" },
];

export const COMPETITION_MATCH_LENGTH_OPTIONS: SelectOption[] = [
	{ value: "90", label: "90" },
	{ value: "80", label: "80" },
	{ value: "70", label: "70" },
	{ value: "60", label: "60" },
	{ value: "45", label: "45" },
];

export const INTERNATIONAL_COUNTRY_CODE = "INT";

export function competitionTypeLabel(type: string | undefined): string {
	const option = COMPETITION_TYPE_OPTIONS.find((item) => item.value === type);
	if (option == null) {
		return type ?? "-";
	}

	return option.label;
}

export function competitionTierLabel(tier: string | null | undefined): string {
	if (tier == null || tier === "") {
		return "-";
	}

	const option = COMPETITION_TIER_OPTIONS.find((item) => item.value === tier);
	if (option == null) {
		return tier;
	}

	return option.label;
}

export function formatCompetitionFormat(
	format: string | null | undefined,
	matchLengthMinutes: number | null | undefined,
): string {
	if (format == null || format === "") {
		if (matchLengthMinutes == null) {
			return "-";
		}

		return String(matchLengthMinutes);
	}

	if (matchLengthMinutes == null) {
		return format;
	}

	return `${format} - ${matchLengthMinutes}`;
}

export function isDomesticCompetitionType(type: CompetitionType | undefined): boolean {
	return type === "domestic_league" || type === "domestic_cup";
}

/**
 * Resolves a stored competition logo for <img src>.
 * Absolute CDN URLs pass through. Relative upload paths are loaded from the API host,
 * which serves public/uploads when the admin app is on its own origin.
 */
export function resolveCompetitionLogoUrl(logoUrl: string | null | undefined): string {
	if (logoUrl == null || logoUrl.trim() === "") {
		return "";
	}

	if (logoUrl.includes("http") || logoUrl.startsWith("//")) {
		return logoUrl;
	}

	const apiBase = apiURL.replace(/\/+$/, "");
	const normalizedPath = logoUrl.replace(/^\/+/, "");
	return `${apiBase}/${normalizedPath}`;
}
